// src/hooks/use-ordering-state.ts
"use client";

import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { isOrderFeatureEnabled } from "@/lib/features";
import { useMarketingLocationContext } from "@/lib/marketing-location-context";
import { composeOrderingState, type OrderingState } from "@/lib/ordering-state";
import type { SectionServiceWindows } from "@convex/lib/serviceWindows";

/**
 * Live storefront ordering state for the active public location. Combines the
 * build-time feature flag, the tenant's static capability, and the live Convex
 * operator switches. While the query loads, defaults optimistically to enabled.
 */
export function useOrderingState(): OrderingState & {
  /**
   * Per-section service hours (e.g. a brunch section that only runs Sunday
   * mornings), straight from this location's order settings. Undefined for any
   * tenant that configured none, which leaves every section visible.
   */
  serviceWindows: SectionServiceWindows | undefined;
} {
  const { location } = useMarketingLocationContext();
  const live = useQuery(api.orderSettings.getPublicOrderingState, {
    locationId: location.id,
  });
  return {
    ...composeOrderingState({
      buildEnabled: isOrderFeatureEnabled(),
      location,
      live,
    }),
    serviceWindows: live?.serviceWindows,
  };
}
